import { scrubPII, assertScrubbed } from './scrubPII';
import { getAuth } from 'firebase/auth';

/**
 * buildCoachContext
 *
 * Receives a manually assembled flat rawMetrics object (scalars only,
 * no arrays) and the raw leads array. Scrubs metrics through scrubPII().
 * Extracts only aggregate counts from the leads array — no records pass
 * through. Returns a safe context object for the coach system prompt.
 *
 * rawMetrics must be assembled at the call site (AIChatbot.jsx) as:
 * {
 *   repName, reportingMonth, reportingYear,
 *   unitsSold, monthlyGoal, commissionMTD, totalGrossMTD,
 *   callsMade, textsSent, emailsSent,
 *   leadsContacted, leadsClosed,
 *   apptSet, apptShown, apptClosed,
 *   incomeGoal
 * }
 * Scalars only. Not the full AppContext. Not useRawReportData output.
 */
export function buildCoachContext({ currentTab, rawMetrics, rawLeadsArray }) {
  const safeMetrics = scrubPII(rawMetrics);
  assertScrubbed(safeMetrics, 'buildCoachContext');

  // Extract aggregate counts from leads array.
  // The array is consumed here and discarded — only counts leave.
  // No names, phones, emails, or notes pass through.
  const safeLeadSummary = rawLeadsArray ? {
    total:        rawLeadsArray.length,
    hot:          rawLeadsArray.filter(l => l.temperature === 'Hot').length,
    warm:         rawLeadsArray.filter(l => l.temperature === 'Warm').length,
    cold:         rawLeadsArray.filter(l => l.temperature === 'Cold').length,
    withFollowUp: rawLeadsArray.filter(l => l.nextFollowUp).length,
    avgAgeDays:   computeAvgLeadAgeDays(rawLeadsArray),
  } : null;

  return {
    currentTab,
    metrics:     safeMetrics,
    leadSummary: safeLeadSummary,
  };
}

function computeAvgLeadAgeDays(leads) {
  if (!leads?.length) return null;
  const now  = Date.now();
  const ages = leads
    .filter(l => l.createdAt)
    .map(l => Math.floor((now - new Date(l.createdAt).getTime()) / 86400000));
  return ages.length > 0
    ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length)
    : null;
}

export function buildCoachSystemPrompt() {
  return `You are the Tracksy AI Sales Coach — a senior dealership performance advisor.

You help car sales professionals improve performance through specific, measurable, dealership-relevant coaching.

WHAT YOU HAVE ACCESS TO:
- The rep's aggregated performance metrics (units, goals, activity counts, conversion rates)
- Pipeline summary counts (lead temperature totals, follow-up status, average lead age)
- The rep's own commission and gross averages

COACHING RULES:
- Every recommendation must reference a specific metric from the data provided
- Never say "work harder", "follow up more", "attend training", or any generic advice
- Think like a GSM who has reviewed this rep's numbers carefully
- Be direct, specific, and realistic for a dealership environment
- Acknowledge when data is insufficient rather than guessing

PRIVACY INSTRUCTION — ABSOLUTE:
You do not have access to individual customer records. You cannot see customer names,
phone numbers, email addresses, or any personally identifiable information. If a user
asks you to look up, reference, or reason about a specific customer by name or contact
detail, explain that you work only with aggregated performance data and cannot access
individual customer records. Direct them to use the Leads tab to review specific contacts
themselves. Do not attempt to recall or reconstruct any customer information.`;
}

/**
 * sendCoachMessage — routes all coach messages through /api/chat.
 * Never calls OpenAI directly. Strips _scrubAudit before sending.
 */
export async function sendCoachMessage({ messages, context }) {
  assertScrubbed(context.metrics, 'sendCoachMessage');

  const auth = getAuth();
  const uid  = auth.currentUser?.uid ?? null;

  const { _scrubAudit, ...safeMetrics } = context.metrics;

  const response = await fetch('/api/chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(uid ? { 'x-uid': uid } : {}),
    },
    body: JSON.stringify({
      messages,
      systemPrompt: buildCoachSystemPrompt(),
      context: {
        currentTab:  context.currentTab,
        metrics:     safeMetrics,
        leadSummary: context.leadSummary,
      },
    }),
  });

  if (!response.ok) throw new Error(`Coach API error: ${response.status}`);
  return response.json();
}
